/**
 * Vertical axis for a chart of pound amounts.
 *
 * The charts used to print every tick in millions to one decimal place, and
 * let the chart library pick the steps. A quarter worth a few hundred thousand,
 * or a filtered channel, read "£0.0m, £0.1m, £0.1m"; a £2.04m maximum produced
 * steps of 0.55m, shown as "£0.6m, £1.1m, £1.7m". Neither says much.
 *
 * Steps here are always 1, 2, 2.5 or 5 times a power of ten, so ticks land on
 * round amounts, and each tick is written in the unit that suits it — £250k,
 * £1m, £1.25m — so no two ticks ever read the same.
 */

/** Most gaps between ticks: the smallest round step that fits this many. */
const MAX_INTERVALS = 6;

/** Room above the tallest bar for the value printed on top of it. */
const LABEL_HEADROOM = 1.08;

const NICE_MULTIPLES = [1, 2, 2.5, 5, 10];

/* The smallest round step that reaches `ceiling` within MAX_INTERVALS — so the
   axis is as fine-grained as it can be without crowding, and the top tick sits
   just above the tallest bar rather than a whole step beyond it. */
const niceStep = (ceiling: number): number => {
  const magnitude = 10 ** Math.floor(Math.log10(ceiling / MAX_INTERVALS));
  const steps = [...NICE_MULTIPLES.map((m) => m * magnitude), 20 * magnitude];
  return steps.find((step) => Math.ceil(ceiling / step) <= MAX_INTERVALS) ?? steps[steps.length - 1];
};

/** Drops trailing zeros: 1.50 -> "1.5", 2.00 -> "2". */
const trim = (value: number, decimals: number): string => String(Number(value.toFixed(decimals)));

/** One tick, e.g. "£0", "£750", "£250k", "£1m", "£1.25m". */
export const formatAxisCurrency = (value: number): string => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount === 0) return '£0';
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) return `${sign}£${trim(abs / 1_000_000, 2)}m`;
  if (abs >= 1_000) return `${sign}£${trim(abs / 1_000, 1)}k`;
  return `${sign}£${Math.round(abs)}`;
};

/** Ticks from zero up past `max`, on a round step. */
export const currencyTicks = (max: number): number[] => {
  const ceiling = Math.max(0, Number(max) || 0) * LABEL_HEADROOM;
  if (ceiling <= 0) return [0];
  const step = niceStep(ceiling);
  const top = Math.ceil(ceiling / step) * step;
  const ticks: number[] = [];
  for (let tick = 0; tick <= top + step / 2; tick += step) ticks.push(Number(tick.toFixed(6)));
  return ticks;
};

/**
 * Props for a Recharts `YAxis` showing pound amounts up to `max`:
 * `<YAxis {...currencyAxisProps(max)} />`.
 */
export const currencyAxisProps = (max: number) => {
  const ticks = currencyTicks(max);
  return {
    domain: [0, ticks[ticks.length - 1] || 1] as [number, number],
    ticks,
    interval: 0 as const,
    tickFormatter: formatAxisCurrency
  };
};

/** The largest value among `keys` across `rows`, or their sum when stacked. */
export const maxOf = <T extends object>(rows: T[], keys: readonly string[], stacked = false): number =>
  rows.reduce((highest, row) => {
    const values = keys.map((key) => Number((row as Record<string, unknown>)[key]) || 0);
    const value = stacked ? values.reduce((sum, v) => sum + v, 0) : Math.max(0, ...values);
    return Math.max(highest, value);
  }, 0);
